import { Permission } from "../../../types/permissions";
import {
  getMenuNodeFlags,
  PermissionMode,
  resolveTreeKeyToActionKeys,
} from "../../setupWizard/utils/roleMenu";
import type { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";

/**
 * "One switch enables a whole view" — for the Roles editor's Sidebar block.
 *
 * Turning a sidebar row on only grants its SCREEN gate. The screen then calls
 * a dozen endpoints, each authorised server-side by its own tree_key, so a role
 * with just the gate gets an empty page. An operator configuring a role cannot
 * be expected to know which keys those are, so a bundle lists them and the
 * switch enables the lot on the first click, after telling the operator exactly
 * what it is about to change — and lets them keep any they do not want.
 *
 * A bundle is edit-time only: it NEVER leaves the frontend and is NEVER
 * persisted. `roles.menu` still carries the real per-key grants.
 *
 * SHARED PERMISSIONS. Several bundles need the same key (`servers-view` is read
 * by every control-panel tab). Turning a bundle OFF therefore removes only the
 * keys that no OTHER still-enabled bundle needs — "GET Users" used by two
 * blocks goes off when both are off, and not before. What a role holds beyond
 * that (say `servers-view` granted by hand for the Servers page) is offered in
 * the confirmation with its switch ON, so it can be kept.
 *
 * REQUIRES. A bundle may `require` another (every control-panel block needs the
 * Banks block: a lift name to click, a bank card to click it on). Enabling a
 * block also enables what it requires; disabling a block also disables every
 * enabled block that requires it, and says so.
 *
 * Every key must be a LEAF action tree_key. A page key would expand to every
 * action under it (`organizations` is the whole Organizations page).
 *
 * To add a bundle for another view, add an entry to PERMISSION_BUNDLES.
 */
export interface PermissionBundleGroup {
  /** Stable id; also the i18n suffix under `organizationManagement.bundles.<id>`. */
  id: string;
  /** Shown when the translation is missing. */
  fallbackLabel: string;
  fallbackReason: string;
  treeKeys: Permission[];
}

export interface PermissionBundle {
  /** Stable id, referenced by other bundles' `requires`. */
  id: string;
  /** The sidebar/screen gate whose switch this bundle belongs to. */
  gateKey: Permission;
  /** Ids of bundles that must be on for this one to be usable. */
  requires: string[];
  groups: PermissionBundleGroup[];
}

// ---------------------------------------------------------------------------
// Monitoring Dashboard (`/v3/monitoring`)
//
// Derived from the calls the page tree under src/pages/monitoringManagement
// makes, matched to the permission catalogue (LiftCloud permissions/catalogue.go) by (uri, verb), and from the
// tab lists of ControlPanelBank / ControlPanelLift (see
// types/monitoringAccess.ts, which the panels themselves read).
//
// DELIBERATELY NOT INCLUDED — grant by hand where wanted:
//  - `users-update`         `PUT /api/users/:id` (saving a preferred grid) is the
//                           general user-edit endpoint; granting it here would hand
//                           out user administration.
//  - `orgs-settings-update` the header menu's door options write org settings.
//  - `monitor-emulator`     STAGING-only connection dropdown.
//  - `monitor-grid-type-map-view`, `maps-view`
//                           Map View, whose menu entry is commented out.
//  - `roles-view`, `roles-view-light`
//                           only the master-admin organisation/role switcher.
//  - `banks-by-server-view` setup wizard only.
//  - `monitor-control-panel-banks` / `-lifts`
//                           opening a panel is now DERIVED from its tabs.
//  - `monitor-control-panel-bank-configuration`, `-secure-car-calls`,
//    `-secure-hall-calls`   read only by components no screen renders.
// ---------------------------------------------------------------------------

/** Every control-panel tab opens with the server the panel belongs to. */
const PANEL_SERVER_GROUP: PermissionBundleGroup = {
  id: "monitoringPanelServer",
  fallbackLabel: "Control panel header",
  fallbackReason: "Reads the server the opened panel belongs to.",
  treeKeys: [Permission.ServersView],
};

/** The action rows a bank is configured with, and the command sender. */
const ACTION_COMMAND_KEYS = [Permission.ActionBanksByBankView, Permission.MonitorGridCommandCreate];

/**
 * Monitoring Dashboard Banks — the select that picks a group of banks, and the
 * bank cards with their lifts and animations. Deliberately WITHOUT the bank-name
 * and lift-name clicks: those open the control panels and belong to the
 * blocks below. This is the minimum for "a bank with lifts" to be on screen.
 */
export const MONITORING_BANKS_BUNDLE: PermissionBundle = {
  id: "banks",
  gateKey: Permission.ViewMonitoringDashboard,
  requires: [],
  groups: [
    {
      id: "banksPageData",
      fallbackLabel: "Banks · page data",
      fallbackReason: "Loads the organization tree, the monitor views and the saved grid.",
      treeKeys: [
        Permission.MonitorView,
        Permission.MonitorGridByOrganization,
        Permission.MonitorGridView,
        Permission.OrgsViewLight,
        Permission.UsersMeView,
      ],
    },
    {
      id: "banksLiveData",
      fallbackLabel: "Banks · live data",
      fallbackReason: "Opens the real-time feed and reads each bank's configuration.",
      treeKeys: [
        Permission.MonitorGridWsView,
        Permission.BanksView,
        Permission.KeysViewLight,
        Permission.DebounceOverridesView,
      ],
    },
  ],
};

export const MONITORING_ACTIONS_BUNDLE: PermissionBundle = {
  id: "actions",
  gateKey: Permission.MonitorControlPanelActions,
  requires: ["banks"],
  groups: [
    {
      id: "actionsCommands",
      fallbackLabel: "Actions · commands",
      fallbackReason: "Reads a bank's configured actions and sends their commands.",
      treeKeys: ACTION_COMMAND_KEYS,
    },
    {
      id: "actionsCalls",
      fallbackLabel: "Actions · call pads",
      fallbackReason: "Picks the car-call and hall-call tabs when the panel opens.",
      treeKeys: [Permission.MonitorControlPanelCarCalls, Permission.MonitorControlPanelHallCalls],
    },
    PANEL_SERVER_GROUP,
  ],
};

export const MONITORING_FAULTS_BUNDLE: PermissionBundle = {
  id: "faults",
  gateKey: Permission.MonitorControlPanelFaults,
  requires: ["banks"],
  groups: [
    {
      id: "faultsData",
      fallbackLabel: "Faults · data",
      fallbackReason: "Searches the recent faults of the bank or lift.",
      treeKeys: [Permission.FaultRecentCreate],
    },
    PANEL_SERVER_GROUP,
  ],
};

export const MONITORING_STATUS_BUNDLE: PermissionBundle = {
  id: "status",
  gateKey: Permission.MonitorControlPanelStatus,
  requires: ["banks"],
  groups: [
    {
      id: "statusData",
      fallbackLabel: "Status · data",
      fallbackReason: "Reads the bank's messages the Status tab shows.",
      treeKeys: [Permission.BanksMessagesDelete],
    },
    PANEL_SERVER_GROUP,
  ],
};

export const MONITORING_STATE_BUNDLE: PermissionBundle = {
  id: "state",
  gateKey: Permission.MonitorControlPanelState,
  requires: ["banks"],
  groups: [
    {
      id: "stateData",
      fallbackLabel: "State · data",
      fallbackReason: "Reads the adapter and its keys the State tab decodes.",
      treeKeys: [Permission.AdaptersView, Permission.KeysView],
    },
    PANEL_SERVER_GROUP,
  ],
};

/**
 * VIP. No tab in either control panel reads `monitor-control-panel-vip-mode`:
 * VIP runs as a sequenced action inside the Actions tab, so this block gates no
 * screen of its own and never makes a name clickable. What it owns is the
 * commands that sequence needs, which is why it requires Actions.
 */
export const MONITORING_VIP_BUNDLE: PermissionBundle = {
  id: "vip",
  gateKey: Permission.MonitorControlPanelVipMode,
  requires: ["actions"],
  groups: [
    {
      id: "vipCommands",
      fallbackLabel: "VIP · commands",
      fallbackReason: "Reads the configured VIP action and sends its commands.",
      treeKeys: ACTION_COMMAND_KEYS,
    },
  ],
};

export const MONITORING_SCHEDULED_ACTIONS_BUNDLE: PermissionBundle = {
  id: "scheduledActions",
  gateKey: Permission.ScheduledActionsView,
  requires: ["banks"],
  groups: [
    {
      id: "scheduledActionsData",
      fallbackLabel: "Scheduled actions · edit",
      fallbackReason: "Create, edit and delete scheduled actions from the dashboard.",
      treeKeys: [
        Permission.ScheduledActionsCreate,
        Permission.ScheduledActionsUpdate,
        Permission.ScheduledActionsDelete,
      ],
    },
    PANEL_SERVER_GROUP,
  ],
};

/**
 * Grids — every grid action: the Saved Grids picker, Save Grid, Manage Grids
 * (list and delete) and grid edit. Gated on `monitor-grid-create`, the key the
 * header's Save Grid entry already reads.
 */
export const MONITORING_GRIDS_BUNDLE: PermissionBundle = {
  id: "grids",
  gateKey: Permission.MonitorGridCreate,
  requires: ["banks"],
  groups: [
    {
      id: "gridsManage",
      fallbackLabel: "Grids · manage",
      fallbackReason: "List a user's grids, delete them and edit a grid's layout.",
      treeKeys: [
        Permission.MonitorGridByUser,
        Permission.MonitorGridDelete,
        Permission.MonitorGridUpdate,
      ],
    },
  ],
};

/** The eight Monitoring Dashboard blocks, in the sidebar's order. */
export const MONITORING_BUNDLES: PermissionBundle[] = [
  MONITORING_BANKS_BUNDLE,
  MONITORING_ACTIONS_BUNDLE,
  MONITORING_FAULTS_BUNDLE,
  MONITORING_STATUS_BUNDLE,
  MONITORING_STATE_BUNDLE,
  MONITORING_VIP_BUNDLE,
  MONITORING_SCHEDULED_ACTIONS_BUNDLE,
  MONITORING_GRIDS_BUNDLE,
];

// ---------------------------------------------------------------------------
// Organization Management (`/v3/organization-management/*`)
//
// Unlike Monitoring, a row here turns on EVERYTHING its screen can do — the
// reads AND every button it offers (create, edit, delete, block, ...): the
// operator drops what they do not want in the confirmation. Derived from the
// calls the pages under src/pages/organizationManagement and
// src/pages/userManagement make, matched to the permission catalogue (LiftCloud permissions/catalogue.go) by
// (uri, verb). No row REQUIRES another: Users and Roles need the KEYS
// `orgs-view`, `orgs-view-light` and `roles-view` (their pickers), not the
// Organizations or Roles screen.
//
// DELIBERATELY NOT INCLUDED — grant by hand where wanted:
//  - `orgs-settings-update`  only the setup wizard and the monitoring header write it.
//  - `orgs-parents-view`, `roles-view-light`, `users-me-view`  no call from these screens.
//  - `users-resend-update`, `users-set-update`, `users-terms-create`, and the
//    self-service `users-offline-psw-*` / `users-action-firing-mode-update`  no Users-screen caller.
//  - the Organizations import / upgrade / export calls  no catalogue key matches them.
// ---------------------------------------------------------------------------

/**
 * Organizations — and Drafts, whose sidebar row shares this gate
 * (screenPermissions.ts: `drafts` reads the organizations permission).
 */
export const ORGANIZATIONS_BUNDLE: PermissionBundle = {
  id: "organizations",
  gateKey: Permission.ViewOrganizationManagementOrganizations,
  requires: [],
  groups: [
    {
      id: "orgsList",
      fallbackLabel: "Organizations · page data",
      fallbackReason: "Lists the organizations, the drafts, and feeds the table's export.",
      treeKeys: [Permission.OrgsView],
    },
    {
      id: "orgsEdit",
      fallbackLabel: "Organizations · create / edit",
      fallbackReason: "The Edit action and the setup wizard's create and update calls.",
      treeKeys: [Permission.OrgsUpdate, Permission.OrgsCreate],
    },
    {
      id: "orgsDelete",
      fallbackLabel: "Organizations · delete",
      fallbackReason: "Deletes an organization or a draft.",
      treeKeys: [Permission.OrgsDelete],
    },
    {
      id: "orgsHolidays",
      fallbackLabel: "Organizations · holidays",
      fallbackReason: "The Holidays drawer opened from a row: list, add, edit and remove.",
      treeKeys: [
        Permission.HolidaysView,
        Permission.HolidaysCreate,
        Permission.HolidaysUpdate,
        Permission.HolidaysDelete,
      ],
    },
  ],
};

/** Roles — the list, and the create / edit / view form (the editor itself calls nothing). */
export const ROLES_BUNDLE: PermissionBundle = {
  id: "roles",
  gateKey: Permission.ViewOrganizationManagementRoles,
  requires: [],
  groups: [
    {
      id: "rolesList",
      fallbackLabel: "Roles · page data",
      fallbackReason: "Lists the roles, loads one to view or edit, and names its organization.",
      treeKeys: [Permission.RolesView, Permission.OrgsViewLight, Permission.OrgsView],
    },
    {
      id: "rolesCreateEdit",
      fallbackLabel: "Roles · create / edit",
      fallbackReason: "Saves a new role, or the changes to an existing one.",
      treeKeys: [Permission.RolesCreate, Permission.RolesUpdate],
    },
    {
      id: "rolesDelete",
      fallbackLabel: "Roles · delete",
      fallbackReason: "Deletes a role.",
      treeKeys: [Permission.RolesDelete],
    },
  ],
};

/** Users — the list, and the create / edit form with every row action. */
export const USERS_BUNDLE: PermissionBundle = {
  id: "users",
  gateKey: Permission.ViewOrganizationManagementUsers,
  requires: [],
  groups: [
    {
      id: "usersList",
      fallbackLabel: "Users · page data",
      fallbackReason: "Lists and loads users, and fills the role and organization pickers.",
      treeKeys: [
        Permission.UsersView,
        Permission.RolesView,
        Permission.OrgsViewLight,
        Permission.OrgsView,
      ],
    },
    {
      id: "usersCreateEdit",
      fallbackLabel: "Users · create / edit",
      fallbackReason: "Saves a new user, or the changes to an existing one.",
      treeKeys: [Permission.UsersCreate, Permission.UsersUpdate],
    },
    {
      id: "usersDelete",
      fallbackLabel: "Users · delete",
      fallbackReason: "Deletes a user.",
      treeKeys: [Permission.UsersDelete],
    },
    {
      id: "usersAccess",
      fallbackLabel: "Users · block / unblock / reset",
      fallbackReason: "Blocks or unblocks a user and resets their password.",
      treeKeys: [
        Permission.UsersBlockUpdate,
        Permission.UsersUnblockUpdate,
        Permission.UsersResetUpdate,
      ],
    },
    {
      id: "usersOffline",
      fallbackLabel: "Users · offline password",
      fallbackReason: "Reads and sets a user's offline password.",
      treeKeys: [Permission.UsersOfflinePswUserView, Permission.UsersOfflinePswUserCreate],
    },
    {
      id: "usersFiringMode",
      fallbackLabel: "Users · action firing mode",
      fallbackReason: "The firing-mode selector on the edit form, a grant of its own.",
      treeKeys: [Permission.UsersActionFiringModeUserUpdate],
    },
  ],
};

/** The Organization Management rows, in the sidebar's order (Drafts shares Organizations). */
export const ORGANIZATION_BUNDLES: PermissionBundle[] = [
  ORGANIZATIONS_BUNDLE,
  ROLES_BUNDLE,
  USERS_BUNDLE,
];

// ---------------------------------------------------------------------------
// Reports (`/v3/reports/*`) — one bundle per report screen, 34 in all
//
// Every report screen is standalone (nothing requires another) and is built the
// same way: ReportsHeader mounts ONE selector chosen by the screen's level
// (servers, banks or lifts — every row fixes its level), the report's own data
// call, and, for the two reports with the Exclusions drawer, the holidays list.
// So a bundle is [level filter, data call, extras].
//
// The filter keys (`*-view-light`, `holidays-view`) are authorised server-side.
// The data keys (`lift-records-*`, `fault-*`) are the catalogue's only entries
// for the reports, matched by name and description — the catalogue has no key
// for the real /reports/* paths, and LiftCloud does not check them today. They
// are listed because they are the reports' permissions in the catalogue, and a
// role that holds them stays correct if the backend ever starts to.
//
// NO KEY EXISTS for `POST /reports/frequency/{lift,fault}` (Frequency By Car /
// Fault) nor for `/reports/all-banks/interruptions` (the expanded row of
// Performance and Beneficial Usage): those rows list only their filter.
//
// DELIBERATELY NOT INCLUDED: `orgs-view` (only the ORGANIZATIONS-level filter,
// which no sidebar row renders), `offset-type-view` (the offsets drawer is hidden
// on every row), `view-report-management-car-service-service-log-chart` (a
// screen gate for a route with no sidebar row).
// ---------------------------------------------------------------------------

const REPORT_SERVER_FILTER: PermissionBundleGroup = {
  id: "reportsServerFilter",
  fallbackLabel: "Reports · server selector",
  fallbackReason: "Lists the servers to pick the report's scope from.",
  treeKeys: [Permission.ServersViewLight],
};
const REPORT_BANK_FILTER: PermissionBundleGroup = {
  id: "reportsBankFilter",
  fallbackLabel: "Reports · bank selector",
  fallbackReason: "Lists the banks to pick the report's scope from.",
  treeKeys: [Permission.BanksViewLight],
};
const REPORT_LIFT_FILTER: PermissionBundleGroup = {
  id: "reportsLiftFilter",
  fallbackLabel: "Reports · lift selector",
  fallbackReason: "Lists the lifts to pick the report's scope from.",
  treeKeys: [Permission.LiftsLightView],
};
const REPORT_HOLIDAYS_FILTER: PermissionBundleGroup = {
  id: "reportsExclusionsHolidays",
  fallbackLabel: "Reports · exclusions (holidays)",
  fallbackReason: "The Exclusions filter loads the organization's holidays.",
  treeKeys: [Permission.HolidaysView],
};

const reportData = (
  id: string,
  label: string,
  reason: string,
  treeKey: Permission,
): PermissionBundleGroup => ({
  id,
  fallbackLabel: label,
  fallbackReason: reason,
  treeKeys: [treeKey],
});

const REPORT_OUT_OF_SERVICE_DATA = reportData(
  "reportsOutOfServiceData",
  "Reports · out of service data",
  "Reads the out-of-service report.",
  Permission.LiftRecordsOutOfServiceView,
);
const REPORT_GENERAL_DATA = reportData(
  "reportsGeneralData",
  "Reports · general data",
  "Reads the escalator-in-use and fire-service reports.",
  Permission.LiftRecordsGeneralView,
);
const REPORT_PERFORMANCE_DATA = reportData(
  "reportsPerformanceData",
  "Reports · performance data",
  "Reads the performance report.",
  Permission.LiftRecordsPerformanceView,
);
const REPORT_BENEFICIAL_DATA = reportData(
  "reportsBeneficialData",
  "Reports · beneficial usage data",
  "Reads the beneficial usage report.",
  Permission.LiftRecordsBeneficialView,
);
const REPORT_FAULT_RECENT_DATA = reportData(
  "reportsFaultRecentData",
  "Reports · recent faults data",
  "Searches the recent faults.",
  Permission.FaultRecentCreate,
);
const REPORT_FAULT_LOG_DATA = reportData(
  "reportsFaultLogData",
  "Reports · fault log data",
  "Reads the fault log.",
  Permission.FaultLogCreate,
);
const REPORT_FAULT_CHART_DATA = reportData(
  "reportsFaultChartData",
  "Reports · fault chart data",
  "Reads the data of the fault charts.",
  Permission.FaultChartChart,
);
const REPORT_TRAFFIC_DATA = reportData(
  "reportsTrafficData",
  "Reports · traffic data",
  "Reads the traffic reports and charts.",
  Permission.LiftRecordsTrafficView,
);
const REPORT_CAR_USE_DATA = reportData(
  "reportsCarUseData",
  "Reports · car use data",
  "Reads the car use charts.",
  Permission.LiftRecordsCarView,
);
const REPORT_SERVICE_LOG_DATA = reportData(
  "reportsServiceLogData",
  "Reports · service log data",
  "Reads the service log, its chart and the statistics.",
  Permission.LiftRecordsServiceView,
);

const reportBundle = (gateKey: Permission, groups: PermissionBundleGroup[]): PermissionBundle => ({
  id: `report:${gateKey}`,
  gateKey,
  requires: [],
  groups,
});

const S = REPORT_SERVER_FILTER;
const B = REPORT_BANK_FILTER;
const L = REPORT_LIFT_FILTER;

/** Every report row of the sidebar, in the sidebar's order. */
export const REPORT_BUNDLES: PermissionBundle[] = [
  // General
  reportBundle(Permission.ViewReportManagementGeneralCurrentOutOfService, [
    S,
    REPORT_OUT_OF_SERVICE_DATA,
  ]),
  reportBundle(Permission.ViewReportManagementGeneralEscalatorInUse, [S, REPORT_GENERAL_DATA]),
  reportBundle(Permission.ViewReportManagementGeneralPerformance, [
    S,
    REPORT_PERFORMANCE_DATA,
    REPORT_HOLIDAYS_FILTER,
  ]),
  reportBundle(Permission.ViewReportManagementGeneralBeneficialUsage, [
    B,
    REPORT_BENEFICIAL_DATA,
    REPORT_HOLIDAYS_FILTER,
  ]),
  reportBundle(Permission.ViewReportManagementGeneralFireService, [S, REPORT_GENERAL_DATA]),
  // Faults
  reportBundle(Permission.ViewReportManagementFaultsRecentFaults, [B, REPORT_FAULT_RECENT_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsRecentFaultsEscmov, [
    B,
    REPORT_FAULT_RECENT_DATA,
  ]),
  reportBundle(Permission.ViewReportManagementFaultsLogByBank, [B, REPORT_FAULT_LOG_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsFrequencyByCar, [S]),
  reportBundle(Permission.ViewReportManagementFaultsFrequencyByFault, [S]),
  reportBundle(Permission.ViewReportManagementFaultsBycar, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByCarEscmov, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByFloor, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByDayWeek, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByDayWeekEscmov, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByType, [B, REPORT_FAULT_CHART_DATA]),
  reportBundle(Permission.ViewReportManagementFaultsByTypeEscmov, [B, REPORT_FAULT_CHART_DATA]),
  // Traffic
  ...[
    Permission.ViewReportManagementTrafficDetailedSummaryReport,
    Permission.ViewReportManagementTrafficHallCallSummaryReport,
    Permission.ViewReportManagementTrafficLongestWaitTimesList,
    Permission.ViewReportManagementTrafficTripsToAndFromFloor,
    Permission.ViewReportManagementTrafficHallButtonCountsByFloor,
    Permission.ViewReportManagementTrafficHallButtonCountByTimeOfDay,
    Permission.ViewReportManagementTrafficAverageByFloor,
    Permission.ViewReportManagementTrafficAverageByTimeOfDay,
    Permission.ViewReportManagementTrafficDistributionByHourlyInterval,
    Permission.ViewReportManagementTrafficDistributionDetailedTotal,
    Permission.ViewReportManagementTrafficDistributionDetaileDirection,
  ].map((gate) => reportBundle(gate, [B, REPORT_TRAFFIC_DATA])),
  // Car Use
  reportBundle(Permission.ViewReportManagementCarUseByHour, [L, REPORT_CAR_USE_DATA]),
  reportBundle(Permission.ViewReportManagementCarUseComparisons, [B, REPORT_CAR_USE_DATA]),
  reportBundle(Permission.ViewReportManagementCarUseDoorTimes, [B, REPORT_CAR_USE_DATA]),
  // Service Logs
  reportBundle(Permission.ViewReportManagementCarServiceServiceLogTable, [
    B,
    REPORT_SERVICE_LOG_DATA,
  ]),
  reportBundle(Permission.ViewReportManagementCarServiceLog, [B, REPORT_SERVICE_LOG_DATA]),
  reportBundle(Permission.ViewReportManagementCarServiceStatistics, [B, REPORT_SERVICE_LOG_DATA]),
];

// ---------------------------------------------------------------------------
// Edge Management (`/v3/edge-management/*`)
//
// Like Organization Management, a row turns on everything its screen can do —
// reads and every button. Derived from the calls under
// src/pages/edgeManagement (and the adapter editor / setup-wizard server step
// they render), matched to the permission catalogue (LiftCloud permissions/catalogue.go) by (uri, verb), and
// checked against LiftCloud's `utils.Authorize`. No row requires another.
//
// DELIBERATELY NOT INCLUDED — grant by hand where wanted:
//  - `adapters-update-action`  only the adapter-configuration action tables call it.
//  - `adapters-csv-create`, `adapters-history-view`, `servers-import`,
//    `servers-export`, `servers-edge-provision-config-view`, the `modbus-wizard-*`
//    keys  no reachable caller.
//  - `orgs-view` for Servers  the edit form reads it only to show the organization
//    name and swallows a failure.
//  - `data-blocks-catalog`  a page key with no action; it only hides Map View's refresh icon.
// Calls LiftCloud does not menu-authorise (`/adapters/:id/cdo`, `/export/stage/*`,
// `/import/organization/*`, `/mfa/step-up/*`) need no key.
// ---------------------------------------------------------------------------

export const ADAPTERS_BUNDLE: PermissionBundle = {
  id: "adapters",
  gateKey: Permission.ViewEdgeManagementAdapters,
  requires: [],
  groups: [
    {
      id: "adaptersList",
      fallbackLabel: "Adapters · list",
      fallbackReason: "Loads the adapter table, and the editor's adapter, and offers the export.",
      treeKeys: [Permission.AdaptersView, Permission.AdaptersUpdateSummary],
    },
    {
      id: "adaptersEditorData",
      fallbackLabel: "Adapters · editor data",
      fallbackReason: "Loads the protocol list and the LN1 keys the editor decodes.",
      treeKeys: [Permission.ProtocolsViewLight, Permission.KeysView],
    },
    {
      id: "adaptersManage",
      fallbackLabel: "Adapters · create, edit, delete",
      fallbackReason: "Create, duplicate, edit and delete adapters.",
      treeKeys: [Permission.AdaptersCreate, Permission.AdaptersUpdate, Permission.AdaptersDelete],
    },
    {
      id: "adaptersLn1Keys",
      fallbackLabel: "Adapters · LN1 key mappings",
      fallbackReason: "Saving LN1 key mappings writes keys in bulk (create, update and delete).",
      treeKeys: [Permission.KeysCreate, Permission.KeysUpdate, Permission.KeysDelete],
    },
  ],
};

export const MAP_VIEW_BUNDLE: PermissionBundle = {
  id: "mapView",
  gateKey: Permission.ViewEdgeManagementMap,
  requires: [],
  groups: [
    {
      id: "mapViewData",
      fallbackLabel: "Map View · maps",
      fallbackReason: "Lists an organization's maps and the organizations to pick from.",
      treeKeys: [Permission.MapsView, Permission.OrgsView],
    },
    {
      id: "mapViewEdit",
      fallbackLabel: "Map View · create, edit, delete",
      fallbackReason: "Upload a map image, rename it, place pins and delete it.",
      treeKeys: [Permission.MapsCreate, Permission.MapsUpdate, Permission.MapsDelete],
    },
    {
      id: "mapViewConfig",
      fallbackLabel: "Map View · configuration",
      fallbackReason: "The Configuration page lists the organization's banks and lifts to pin.",
      treeKeys: [Permission.BanksViewOrg],
    },
  ],
};

export const SERVERS_BUNDLE: PermissionBundle = {
  id: "servers",
  gateKey: Permission.ViewEdgeManagementServers,
  requires: [],
  groups: [
    {
      id: "serversList",
      fallbackLabel: "Servers · list",
      fallbackReason: "Loads the server table and the edit form, with organization names.",
      treeKeys: [Permission.ServersView, Permission.OrgsViewLight],
    },
    {
      id: "serversManage",
      fallbackLabel: "Servers · create, edit, delete",
      fallbackReason: "Create, edit and delete servers.",
      treeKeys: [Permission.ServersCreate, Permission.ServersUpdate, Permission.ServersDelete],
    },
    {
      id: "serversSecret",
      fallbackLabel: "Servers · client secret",
      fallbackReason: "Reveals the edge server's OAuth client secret (also asks for MFA).",
      treeKeys: [Permission.ServersOauthCreate],
    },
  ],
};

/** The Edge Management rows, in the sidebar's order. */
export const EDGE_BUNDLES: PermissionBundle[] = [ADAPTERS_BUNDLE, MAP_VIEW_BUNDLE, SERVERS_BUNDLE];

/** Every bundle, in the sidebar's order: Monitoring, Organization, Edge, Reports. */
export const PERMISSION_BUNDLES: PermissionBundle[] = [
  ...MONITORING_BUNDLES,
  ...ORGANIZATION_BUNDLES,
  ...EDGE_BUNDLES,
  ...REPORT_BUNDLES,
];

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function getPermissionBundle(gateKey: string | undefined): PermissionBundle | undefined {
  return gateKey ? PERMISSION_BUNDLES.find((bundle) => bundle.gateKey === gateKey) : undefined;
}

export function bundleTreeKeys(bundle: PermissionBundle): Permission[] {
  return Array.from(new Set(bundle.groups.flatMap((group) => group.treeKeys)));
}

/** Everything a bundle needs to be on: its own keys plus its gate. */
function bundleKeysWithGate(bundle: PermissionBundle): Permission[] {
  return [bundle.gateKey, ...bundleTreeKeys(bundle)];
}

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

type Mode = PermissionMode | "both";
type Flags = { active: boolean; offline: boolean; locked: boolean };

/** Granted in EVERY mode asked for — what "already enabled" means when enabling. */
function fullyGranted(flags: Flags, mode: Mode): boolean {
  if (mode === "online") return flags.active;
  if (mode === "offline") return flags.offline;
  return flags.active && flags.offline;
}

/** Granted in ANY mode asked for — what "still there to remove" and "in use" mean. */
function partlyGranted(flags: Flags, mode: Mode): boolean {
  if (mode === "online") return flags.active;
  if (mode === "offline") return flags.offline;
  return flags.active || flags.offline;
}

/**
 * Whether `treeKey` can be granted in `menu` at all. It cannot when the key is
 * absent from the menu, or is a page with no grantable action beneath it (the
 * switch resolves a page to its leaf actions, so an action-less page has nothing
 * to turn on), or is locked while un-granted. Turning such a key "on" does
 * nothing, and says nothing — which is why callers report it instead.
 */
export function isTreeKeyGrantable(menu: RoleMenuT, treeKey: string, mode: Mode): boolean {
  const flags = getMenuNodeFlags(menu, treeKey);
  if (!flags) return false;
  if (fullyGranted(flags, mode)) return true;
  if (flags.locked) return false;
  return resolveTreeKeyToActionKeys(menu, treeKey).length > 0;
}

export interface BundlePlanGroup {
  group: PermissionBundleGroup;
  treeKeys: Permission[];
  /** Set when this group belongs to a bundle the toggled one REQUIRES, not to the toggled one. */
  fromBundle?: PermissionBundle;
}

// ---------------------------------------------------------------------------
// Enable
// ---------------------------------------------------------------------------

export interface BundleEnablePlan {
  /** What switching on would newly grant, by group. Empty groups are dropped. */
  pending: BundlePlanGroup[];
  /**
   * Keys the editor cannot grant: absent from this menu (a role's menu is a
   * snapshot that can predate a key, and the editing user's own menu is the
   * ceiling), a page with no action beneath it to turn on, or locked while
   * un-granted. This includes the toggled gates themselves. The switch skips them silently, so
   * they are reported rather than shown as "will be enabled".
   */
  unavailable: Permission[];
}

/** `toggled` plus, transitively, every bundle they require. */
function expandRequired(toggled: PermissionBundle[]): PermissionBundle[] {
  const required: PermissionBundle[] = [];
  const seen = new Set(toggled.map((bundle) => bundle.id));
  const queue = [...toggled];
  while (queue.length > 0) {
    const current = queue.shift() as PermissionBundle;
    current.requires.forEach((id) => {
      const needed = PERMISSION_BUNDLES.find((bundle) => bundle.id === id);
      if (needed && !seen.has(id)) {
        seen.add(id);
        required.push(needed);
        queue.push(needed);
      }
    });
  }
  return required;
}

/**
 * What turning `toggled` bundles' gates ON would change in `menu`.
 *
 * The toggled gates themselves are not listed as changes (their row's own switch
 * flips them) — only reported under `unavailable` when they cannot be granted;
 * a REQUIRED bundle's gate is listed, because nothing else would turn it on.
 * A key several bundles share is listed once, under the first that names it.
 */
export function planBundlesEnable(
  menu: RoleMenuT,
  toggled: PermissionBundle[],
  mode: Mode,
): BundleEnablePlan {
  const required = expandRequired(toggled);
  const pending: BundlePlanGroup[] = [];
  const unavailable: Permission[] = [];
  const seen = new Set<Permission>();

  const consider = (treeKey: Permission, into: Permission[]) => {
    if (seen.has(treeKey)) return;
    seen.add(treeKey);
    const flags = getMenuNodeFlags(menu, treeKey);
    if (!isTreeKeyGrantable(menu, treeKey, mode)) {
      unavailable.push(treeKey);
    } else if (flags && !fullyGranted(flags, mode)) {
      into.push(treeKey);
    }
  };

  // The toggled gates are not listed as changes (their row's own switch flips
  // them), but one the editor cannot grant must still be reported: the switch
  // skips it without a word, so the row would just stay off.
  toggled.forEach((bundle) => {
    seen.add(bundle.gateKey);
    if (!isTreeKeyGrantable(menu, bundle.gateKey, mode)) unavailable.push(bundle.gateKey);
  });

  [...toggled, ...required].forEach((bundle) => {
    const isRequired = required.includes(bundle);
    bundle.groups.forEach((group, index) => {
      const changing: Permission[] = [];
      if (isRequired && index === 0) consider(bundle.gateKey, changing);
      group.treeKeys.forEach((treeKey) => consider(treeKey, changing));
      if (changing.length > 0) {
        pending.push({
          group,
          treeKeys: changing,
          ...(isRequired ? { fromBundle: bundle } : {}),
        });
      }
    });
  });
  return { pending, unavailable };
}

// ---------------------------------------------------------------------------
// Disable
// ---------------------------------------------------------------------------

export interface BundleDisablePlan {
  /** What switching off would newly turn off, by group: needed by no other enabled bundle. */
  remove: BundlePlanGroup[];
  /** Enabled bundles that require a toggled one, and so go off with it. */
  alsoOff: PermissionBundle[];
  /** Held keys that stay ON because another still-enabled bundle needs them, and who does. */
  kept: Array<{ treeKey: Permission; heldBy: PermissionBundle[] }>;
  /** The gates that flip: the toggled bundles' and their dependents'. */
  gates: Permission[];
}

/**
 * What turning `toggled` bundles' gates OFF would change in `menu`.
 *
 * "Enabled" is read from a bundle's gate: granted in either mode asked for.
 * Keys held by an enabled bundle that is NOT going off with this one are never
 * removed — that is the whole shared-permission rule.
 */
export function planBundlesDisable(
  menu: RoleMenuT,
  toggled: PermissionBundle[],
  mode: Mode,
): BundleDisablePlan {
  const isEnabled = (bundle: PermissionBundle) => {
    const flags = getMenuNodeFlags(menu, bundle.gateKey);
    return !!flags && partlyGranted(flags, mode);
  };

  // Everything going off: the toggled bundles plus, transitively, each enabled
  // bundle that requires one of them.
  const going: PermissionBundle[] = [...toggled];
  const alsoOff: PermissionBundle[] = [];
  for (let i = 0; i < going.length; i += 1) {
    PERMISSION_BUNDLES.forEach((candidate) => {
      if (
        !going.includes(candidate) &&
        candidate.requires.includes(going[i].id) &&
        isEnabled(candidate)
      ) {
        going.push(candidate);
        alsoOff.push(candidate);
      }
    });
  }

  // Sidebar order, not discovery order: it is what the operator reads.
  alsoOff.sort((x, y) => PERMISSION_BUNDLES.indexOf(x) - PERMISSION_BUNDLES.indexOf(y));

  const holders = new Map<Permission, PermissionBundle[]>();
  PERMISSION_BUNDLES.filter((bundle) => !going.includes(bundle) && isEnabled(bundle)).forEach(
    (bundle) => {
      bundleKeysWithGate(bundle).forEach((treeKey) => {
        holders.set(treeKey, [...(holders.get(treeKey) ?? []), bundle]);
      });
    },
  );

  const remove: BundlePlanGroup[] = [];
  const kept: BundleDisablePlan["kept"] = [];
  const seen = new Set<Permission>(going.map((bundle) => bundle.gateKey));

  going.forEach((bundle) => {
    const isDependent = !toggled.includes(bundle);
    bundle.groups.forEach((group) => {
      const removing: Permission[] = [];
      group.treeKeys.forEach((treeKey) => {
        if (seen.has(treeKey)) return;
        seen.add(treeKey);
        const flags = getMenuNodeFlags(menu, treeKey);
        // Nothing to remove when the role does not hold it, or it cannot be changed.
        if (!flags || flags.locked || !partlyGranted(flags, mode)) return;
        const heldBy = holders.get(treeKey);
        if (heldBy && heldBy.length > 0) kept.push({ treeKey, heldBy });
        else removing.push(treeKey);
      });
      if (removing.length > 0) {
        remove.push({
          group,
          treeKeys: removing,
          ...(isDependent ? { fromBundle: bundle } : {}),
        });
      }
    });
  });

  return { remove, alsoOff, kept, gates: going.map((bundle) => bundle.gateKey) };
}
